// src/pages/BlockedPolicies.jsx
import React, { useState, useContext, useEffect } from "react";
import { Input } from "reactstrap";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { ColorModeContext } from "../../index";
import { request } from "../../utils/request";
import "./dashboard.scss";

/* Base palette: Vault Navy, Edge Blue (100, 70, 45, 25%), Slate */
const PALETTE = [
  "#0B2545",
  "#1F6FEB",
  "#6299F1",
  "#9ABEF6",
  "#C7DBFB",
  "#6B7A90",
];

const INVALID_POLICY_VALUES = new Set([
  "",
  "value",
  "null",
  "undefined",
  "n/a",
  "na",
  "none",
  "unknown",
  "-",
  "--",
]);

/** Safe array parser for multiple API response shapes */
function safeArrayFrom(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.data)) return resp.data;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.docs)) return resp.docs;
  if (resp && resp.success && Array.isArray(resp.data)) return resp.data;
  return [];
}

/** Normalize policy label and skip placeholders */
function normalizePolicyName(raw) {
  if (raw === null || raw === undefined) return null;
  const name = String(raw).trim();
  if (!name) return null;

  const lower = name.toLowerCase();
  if (INVALID_POLICY_VALUES.has(lower)) return null;

  return name;
}

/** Get the first valid policy name from a list of candidates */
function firstValidPolicy(...candidates) {
  for (const candidate of candidates) {
    const normalized = normalizePolicyName(candidate);
    if (normalized) return normalized;
  }
  return null;
}

/**
 * Extract policy name from different possible API shapes.
 */
function getPolicyNameFromLog(log) {
  if (!log) return null;

  const ev = log.eventData || {};
  const policyViolation = ev.policyViolation || {};

  return firstValidPolicy(
    policyViolation.policyViolated,
    policyViolation.policyName,
    policyViolation.policy,
    ev.policyViolated,
    ev.policyName,
    ev.policy_name,
    log.policyViolated,
    log.policyName,
    log.policy_name
  );
}

/** Detect if event is policy violation */
function isPolicyViolationType(t) {
  if (!t) return false;
  const s = String(t).toLowerCase().replace(/[\s_-]+/g, "");
  return s.includes("policyviolation") || s.includes("policy_violation");
}

/** Stable unique color by index */
function colorForIndex(index) {
  if (index < PALETTE.length) return PALETTE[index];
  const hue = (index * 137.508) % 360; 
  return `hsl(${hue}, 70%, 50%)`;
}

/** Assign one unique color per policy name */
function buildColorMap(policyNames) {
  const map = new Map();
  policyNames.forEach((name, index) => {
    map.set(name, colorForIndex(index));
  });
  return map;
}

/** Attempt to fetch a large set of logs, fallback to default endpoint */
async function fetchLogsAll() {
  try {
    const r = await request("logs?limit=10000&sort=-createdAt", { method: "GET" });
    const arr = safeArrayFrom(r);
    if (arr.length > 0) return arr;
  } catch (e) {
    console.debug("fetchLogsAll: large-limit fetch failed:", e?.message || e);
  }

  try {
    const r = await request("logs", { method: "GET" });
    return safeArrayFrom(r);
  } catch (e) {
    console.error("fetchLogsAll: fallback logs fetch failed:", e);
    return [];
  }
}

export default function BlockedPolicies() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  const [dept, setDept] = useState("all");
  const [departments, setDepartments] = useState(["all"]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAndBuild = async () => {
    setLoading(true);

    try {
      const [usersResp, logsRaw] = await Promise.all([
        request("user", { method: "GET" }),
        fetchLogsAll(),
      ]);

      const users = safeArrayFrom(usersResp);
      const logs = Array.isArray(logsRaw) ? logsRaw : safeArrayFrom(logsRaw);

      const userDept = new Map();
      const deptSet = new Set();

      for (const u of users) {
        const id = u && (u._id || u.id) ? String(u._id || u.id) : null;
        const d = u && (u.department || "Unassigned")
          ? String(u.department || "Unassigned")
          : "Unassigned";

        if (id) userDept.set(id, d);
        if (d) deptSet.add(d);
      }

      setDepartments(["all", ...Array.from(deptSet).sort((a, b) => a.localeCompare(b))]);

      const counts = new Map();

      for (const lg of logs) {
        const et = lg?.eventType || lg?.event_type || lg?.log_type || "";
        if (!isPolicyViolationType(et)) continue;

        const rawPolicy = getPolicyNameFromLog(lg);
        if (!rawPolicy) continue;

        const uid = lg?.userId
          ? String(lg.userId)
          : lg?.user_id
            ? String(lg.user_id)
            : null;

        const logDept = uid && userDept.has(uid) ? userDept.get(uid) : "Unassigned";
        if (dept !== "all" && logDept !== dept) continue;

        counts.set(rawPolicy, (counts.get(rawPolicy) || 0) + 1);
      }

      const entries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);

      const colorMap = buildColorMap(entries.map(([name]) => name));

      const data = entries.map(([name, value]) => ({
        name,
        value,
        color: colorMap.get(name),
      }));

      setChartData(data);
    } catch (err) {
      console.error("BlockedPolicies fetch error:", err);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndBuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dept]);

  const total = chartData.reduce((s, d) => s + (d.value || 0), 0);

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const p = payload[0].payload;
    const pct = total ? Math.round(((p.value || 0) / total) * 100) : 0;

    return (
      <div
        style={{
          backgroundColor: "var(--canvas)",
          border: "1px solid var(--hairline)",
          padding: 8,
          borderRadius: 6,
          color: "var(--vault-navy)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
        }}
      >
        <div style={{ fontWeight: 600 }}>{p.name}</div>
        <div style={{ fontSize: 13 }}>
          {p.value} occurrence{p.value === 1 ? "" : "s"}
        </div>
        <div className="text-secondary" style={{ fontSize: 12 }}>
          {pct}%
        </div>
      </div>
    );
  };

  const renderLegend = (props) => {
    const { payload } = props || {};
    if (!payload || !payload.length) return null;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {payload.map((entry) => {
          const itemColor =
            entry.color ||
            entry.payload?.fill ||
            entry.payload?.color ||
            "#ccc";

          return (
            <div
              key={`${entry.value}-${itemColor}`}
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  background: itemColor,
                  display: "inline-block",
                  borderRadius: 3,
                }}
              />
              <div style={{ fontSize: 13, color: "var(--vault-navy)" }}>
                {entry.value}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="widget-card" style={{ width: "100%" }}>
      {/* Applied global-card class */}
      <div
        className="global-card"
        style={{
          height: "100%",
          padding: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Dropdown fixed at top-right corner of the card */}
        <div style={{ position: "absolute", top: "0.9rem", right: "0.9rem", zIndex: 5 }}>
          <Input
            type="select"
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            className="global-input"
            style={{
              width: 150,
              padding: "6px 8px",
              backgroundColor: "var(--canvas)",
              color: "var(--vault-navy)",
              border: "1px solid var(--hairline)",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='${isDark ? "%23E6EAF2" : "%230B2545"}' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
              backgroundSize: "12px 12px",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            {departments.map((d) => (
              <option key={d} value={d} style={{ backgroundColor: "var(--canvas)", color: "var(--vault-navy)" }}>
                {d === "all" ? "All Departments" : d}
              </option>
            ))}
          </Input>
        </div>

        <div style={{ padding: "0.9rem 170px 0 0.9rem" }}>
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <h5 className="global-section-title" style={{ margin: 0 }}>
              Blocked Policies
            </h5>
            <div className="text-secondary global-caption" style={{ marginTop: 4 }}>
              {total} total blocked event{total === 1 ? "" : "s"}
              {dept !== "all" ? ` in ${dept}` : ""}
            </div>
          </div>
        </div>

        <div style={{ flex: "1 1 auto", minHeight: 200, display: "flex", gap: 12, padding: "0.6rem 0.9rem 0.9rem 0.9rem", position: "relative" }}>
          <div style={{ flex: "1 1 60%", minHeight: 320, height: "100%", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            
            {loading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark ? "rgba(11, 18, 33, 0.7)" : "rgba(255, 255, 255, 0.7)",
                  zIndex: 6,
                  borderRadius: 8
                }}
              >
                <div className="text-secondary" style={{ fontWeight: 600 }}>Calculating…</div>
              </div>
            )}

            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius="100%"
                  paddingAngle={2}
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={!loading}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="var(--canvas)" /* Ensures chart lines blend with the card background */
                      strokeWidth={1.5}
                    />
                  ))}
                </Pie>

                <Tooltip content={<TooltipContent />} />

                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  iconType="square"
                  content={renderLegend}
                  wrapperStyle={{ right: 0 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}